import { isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, Signal, WritableSignal, inject, signal } from '@angular/core';

import { IStairConfig, railingMaterialsOf, risersEnabledOf } from '../models/stair-config.model';

const STORAGE_KEY: string = 'sc:last-stair-config';

/**
 * Persists the user's most recent stair configuration across routes and reloads.
 * The constructor page writes here on every config change; the home contact
 * form reads it to attach a `config_json` snapshot to outgoing emails so the
 * studio sees what the user was actually building.
 *
 * Hydrated from localStorage on browser init. SSR-safe — reads/writes are
 * skipped when not in a browser platform.
 */
@Injectable({ providedIn: 'root' })
export class LastStairConfigService {
    readonly #platformId: object = inject(PLATFORM_ID);
    readonly #config: WritableSignal<IStairConfig | null> = signal<IStairConfig | null>(null);

    public readonly config: Signal<IStairConfig | null> = this.#config.asReadonly();

    constructor() {
        if (!isPlatformBrowser(this.#platformId)) {
            return;
        }
        try {
            const raw: string | null = globalThis.localStorage.getItem(STORAGE_KEY);
            if (raw !== null) {
                const stored: IStairConfig = JSON.parse(raw) as IStairConfig;
                // A session stored before the railing had species of its own carries the treads' wood;
                // one stored before the riser toggle was drawn with risers.
                this.#config.set({
                    ...stored,
                    ...railingMaterialsOf(stored, stored.treadMaterialId),
                    risersEnabled: risersEnabledOf(stored),
                });
            }
        } catch {
            // localStorage unavailable / parse error — non-fatal.
        }
    }

    public set(config: IStairConfig): void {
        this.#config.set(config);
        if (!isPlatformBrowser(this.#platformId)) {
            return;
        }
        try {
            globalThis.localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
        } catch {
            // quota exceeded / disabled — non-fatal.
        }
    }

    public clear(): void {
        this.#config.set(null);
        if (!isPlatformBrowser(this.#platformId)) {
            return;
        }
        try {
            globalThis.localStorage.removeItem(STORAGE_KEY);
        } catch {
            // non-fatal
        }
    }
}
